import {
  GRID_POSITION,
  GRID_SIZE,
  MODE_ALZADOS,
  MODE_FORCE_ALZADOS,
  SCENE_FLOOR_SHADOW_OPACITY,
} from '@constants'
import { useViewerContext } from '@context/viewers'
import { useThree } from '@react-three/fiber'
import { useAppStore } from '@store'
import { useEffect } from 'react'
import { Color } from 'three'
import { CameraManager3D } from './CameraManager3D'
import { CameraManagerAlzados } from './CameraManagerAlzados'
import { LightsManager3D } from './LightsManager3D'
import { ObjectsManager3D } from './ObjectsManager3D'
import { ItemDimensions3D } from './Item/ItemDimensions3D'
import { ModularSectionToolbarBridge } from './linked/ModularSectionToolbarBridge'
import { ModularFloorDragSafety } from './linked/ModularFloorDragSafety'
import { GeometryIntegrityDebug3D } from './debug/GeometryIntegrityDebug3D'
import { ModularSnapDebug3D } from './debug/ModularSnapDebug3D'
import { MODULAR_SNAP_DEBUG_ENABLED } from '@utils/modularSnap/config'
import { enableModularSnapDebug, useSnapDebugStore } from '@utils/modularSnap/snapDebugStore'

export function Scene3D() {
  const deleteItem = useAppStore.use.deleteSceneObject()
  const coid = useAppStore.use.currentObjectID()
  const isModalOpen = useAppStore.use.isModalOpen()
  const { controls, scene } = useThree()
  const { setControls } = useViewerContext()
  const editWallProperty = useAppStore.use.editWallProperty()
  const deleteSelected = useAppStore.use.deleteSelectedObjects()
  const prefs = useAppStore.use.prefs()
  const items = useAppStore.use.items()
  const mode = useAppStore((store) => store.mode)

  // Fondo del visor: tono cálido muy claro (un poco más claro que #f0efec)
  scene.background = new Color(0xf6f5f3)

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (isModalOpen) return
      const target = e.target as HTMLElement | null
      const isTypingField =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          (target as HTMLElement).isContentEditable)
      if (isTypingField) return
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault()
        // deleteItem(coid)
        deleteSelected()
        // unselectAll()
      }
    }
    if (window) window.addEventListener('keydown', handleKey)
    return () => {
      window.removeEventListener('keydown', handleKey)
      for (const [itemID, itemValue] of items.entries()) {
        if (itemValue.type === 'walls') {
          editWallProperty(itemID, { visible: true })
        }
      }
    }
  }, [editWallProperty, isModalOpen, items, coid, deleteSelected])

  // useEffect(() => {
  //   controls?.fitToBox(scene, true, {
  //     paddingLeft: 0.5,
  //     paddingRight: 0.5,
  //   })
  // }, [])

  const setOBBScene = useAppStore.use.setOBBScene()
  const snapDebugEnabled = useSnapDebugStore((s) => s.enabled)

  useEffect(() => {
    if (controls) setControls(controls)
  }, [controls, setControls])

  useEffect(() => {
    setOBBScene()
    if (MODULAR_SNAP_DEBUG_ENABLED) {
      enableModularSnapDebug()
    }
    if (typeof window !== 'undefined') {
      ;(window as Window & { __enableModularSnapDebug__?: () => void }).__enableModularSnapDebug__ =
        enableModularSnapDebug
    }
  }, [])

  return (
    <group>
      {mode === MODE_ALZADOS || mode === MODE_FORCE_ALZADOS ? (
        <CameraManagerAlzados />
      ) : (
        <CameraManager3D />
      )}
      <LightsManager3D />
      <ObjectsManager3D />
      <ModularSectionToolbarBridge />
      <ModularFloorDragSafety />
      {(MODULAR_SNAP_DEBUG_ENABLED || snapDebugEnabled) && <ModularSnapDebug3D />}
      {import.meta.env.DEV && <GeometryIntegrityDebug3D />}
      {/* Cotas de dimensiones para items seleccionados */}
      <ItemDimensions3D />
      {/* Plano receptor de sombras: blanco idéntico al fondo fuera de la sombra */}
      <group>
        <mesh
          dispose={null}
          position={GRID_POSITION}
          rotation={[-Math.PI / 2, 0, 0]}
          receiveShadow
        >
          <planeGeometry args={[GRID_SIZE * 20, GRID_SIZE * 20]} />
          <shadowMaterial
            transparent
            opacity={SCENE_FLOOR_SHADOW_OPACITY}
          />
        </mesh>
      </group>
    </group>
  )
}
